/**
 * BrandTab — the Tier-2 "Brand" designer (docs/plans/2026-06-26-design-system-theme-token-designer.md).
 * Six plain-language controls (brand colour, accent, heading/body font, corners,
 * spacing) that GENERATE the full token set via theme-engine. Web controls from
 * the Duro design system; a native Brand panel comes with the native editor.
 * Includes a live preview and an inline WCAG-contrast nudge. Writes the new
 * `SiteTheme` shape on every change.
 */
import { useMemo } from "react";
import { css, html } from "react-strict-dom";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import {
  Button,
  Callout,
  ColorInput,
  Field,
  Inline,
  Input,
  Select,
  Stack,
  Text,
  Toggle,
  ToggleGroup,
} from "@duro-app/ui";
import {
  resolveTokens,
  duro,
  brandContrastChecks,
  CURATED_THEMES,
  type CornerRoundness,
  type Density,
  type ContrastCheck,
  type Tokens,
  type BrandInputs,
  type ThemePreset,
} from "@website-builder/theme-engine";
import type { SiteTheme } from "@website-builder/schemas";

const BRAND_KEYS: readonly (keyof BrandInputs)[] = [
  "brandColor",
  "accentColor",
  "headingFont",
  "bodyFont",
  "cornerRoundness",
  "density",
];

function sameBrand(a: BrandInputs, b: BrandInputs): boolean {
  return BRAND_KEYS.every((k) => a[k] === b[k]);
}

const FONTS = [
  "Inter",
  "Poppins",
  "Lora",
  "Playfair Display",
  "Nunito",
  "Georgia",
  "system-ui",
] as const;
const ROUNDNESS: readonly CornerRoundness[] = [
  "none",
  "subtle",
  "rounded",
  "pill",
];
const DENSITY: readonly Density[] = ["compact", "comfortable", "spacious"];

export function BrandTab({
  theme,
  onChange,
  canEdit,
}: {
  readonly theme: SiteTheme;
  readonly onChange: (t: SiteTheme) => void;
  readonly canEdit: boolean;
}) {
  const { tokens, cssVars } = useMemo(
    () => resolveTokens(duro, theme),
    [theme],
  );
  const checks = useMemo(() => brandContrastChecks(tokens), [tokens]);
  const b = theme.brand;
  const set = (patch: Partial<SiteTheme["brand"]>) =>
    onChange({ ...theme, brand: { ...b, ...patch } });
  const applyBrand = (brand: BrandInputs) => onChange({ ...theme, brand });
  const isDefault = sameBrand(b, duro.defaultBrand);

  return (
    <Stack gap="ms">
      <Text as="p" variant="bodySm" color="muted">
        Start from a theme, then make it yours — colours, fonts and shape flow
        to every page.
      </Text>

      <ThemesPicker
        current={b}
        onPick={applyBrand}
        disabled={!canEdit}
        onReset={() => applyBrand(duro.defaultBrand)}
        canReset={canEdit && !isDefault}
      />

      <Field.Root disabled={!canEdit}>
        <Field.Label>Brand colour</Field.Label>
        <ColorField
          value={b.brandColor}
          onChange={(v) => set({ brandColor: v })}
          disabled={!canEdit}
          name="Brand colour"
        />
      </Field.Root>
      <Field.Root disabled={!canEdit}>
        <Field.Label>Accent colour</Field.Label>
        <ColorField
          value={b.accentColor}
          onChange={(v) => set({ accentColor: v })}
          disabled={!canEdit}
          name="Accent colour"
        />
      </Field.Root>
      <Field.Root disabled={!canEdit}>
        <Field.Label>Heading font</Field.Label>
        <FontSelect
          value={b.headingFont}
          onChange={(v) => set({ headingFont: v })}
          label="Heading font"
        />
      </Field.Root>
      <Field.Root disabled={!canEdit}>
        <Field.Label>Body font</Field.Label>
        <FontSelect
          value={b.bodyFont}
          onChange={(v) => set({ bodyFont: v })}
          label="Body font"
        />
      </Field.Root>
      <Field.Root group>
        <Field.Label>Corners</Field.Label>
        <Segmented
          options={ROUNDNESS}
          value={b.cornerRoundness}
          onChange={(v) => set({ cornerRoundness: v })}
          disabled={!canEdit}
        />
      </Field.Root>
      <Field.Root group>
        <Field.Label>Spacing</Field.Label>
        <Segmented
          options={DENSITY}
          value={b.density}
          onChange={(v) => set({ density: v })}
          disabled={!canEdit}
        />
      </Field.Root>

      <ContrastNudge checks={checks} />
      <Preview cssVars={cssVars} tokens={tokens} />
    </Stack>
  );
}

// ---------- Tier 1 · Themes ----------

function ThemesPicker({
  current,
  onPick,
  disabled,
  onReset,
  canReset,
}: {
  readonly current: BrandInputs;
  readonly onPick: (b: BrandInputs) => void;
  readonly disabled: boolean;
  readonly onReset: () => void;
  readonly canReset: boolean;
}) {
  const activeId = CURATED_THEMES.find((t) => sameBrand(t.brand, current))?.id;
  return (
    <Stack gap="xs">
      <Inline justify="between" align="baseline">
        <Text variant="label" weight="semibold">
          Theme
        </Text>
        <Button
          variant="link"
          size="small"
          onClick={onReset}
          disabled={!canReset}
          aria-label="Reset every Brand control to the default theme"
        >
          Reset to default
        </Button>
      </Inline>
      <Inline>
        <ToggleGroup
          aria-label="Theme starting point"
          size="small"
          disabled={disabled}
          value={activeId ? [activeId] : []}
          onValueChange={(ids) => {
            const picked = CURATED_THEMES.find((t) => t.id === ids[0]);
            if (picked) onPick(picked.brand);
          }}
        >
          {CURATED_THEMES.map((t) => (
            <ThemeChip key={t.id} theme={t} />
          ))}
        </ToggleGroup>
      </Inline>
    </Stack>
  );
}

function ThemeChip({ theme }: { readonly theme: ThemePreset }) {
  return (
    <Toggle value={theme.id} aria-label={`${theme.name}: ${theme.description}`}>
      <Inline gap="xs" align="center">
        <html.span
          aria-hidden
          style={[
            s.swatch,
            s.swatchColors(theme.brand.brandColor, theme.brand.accentColor),
          ]}
        />
        {theme.name}
      </Inline>
    </Toggle>
  );
}

// ---------- controls ----------

function ColorField({
  value,
  onChange,
  disabled,
  name,
}: {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly disabled: boolean;
  readonly name: string;
}) {
  return (
    <Inline gap="sm" align="center">
      <ColorInput
        value={normalizeHex(value)}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-label={`${name} picker`}
      />
      <Input
        font="mono"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-label={`${name} hex`}
      />
    </Inline>
  );
}

function FontSelect({
  value,
  onChange,
  label,
}: {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly label: string;
}) {
  const options = FONTS.includes(value as (typeof FONTS)[number])
    ? FONTS
    : ([value, ...FONTS] as readonly string[]);
  return (
    <Select.Root
      value={value}
      onValueChange={(v) => {
        if (v) onChange(v);
      }}
    >
      <Select.Trigger aria-label={label}>
        <Select.Value />
        <Select.Icon />
      </Select.Trigger>
      <Select.Popup>
        {options.map((f) => (
          <Select.Item key={f} value={f}>
            {f}
          </Select.Item>
        ))}
      </Select.Popup>
    </Select.Root>
  );
}

function Segmented<T extends string>({
  options,
  value,
  onChange,
  disabled,
}: {
  readonly options: readonly T[];
  readonly value: T;
  readonly onChange: (v: T) => void;
  readonly disabled: boolean;
}) {
  // Inline so the group hugs its options instead of stretching the row.
  return (
    <Inline>
      <ToggleGroup
        size="small"
        disabled={disabled}
        value={[value]}
        onValueChange={(v) => {
          // A single-selection group can be emptied by pressing the active
          // item again; the brand always has a value, so ignore that.
          const next = options.find((o) => o === v[0]);
          if (next) onChange(next);
        }}
      >
        {options.map((opt) => (
          <Toggle key={opt} value={opt}>
            {capitalize(opt)}
          </Toggle>
        ))}
      </ToggleGroup>
    </Inline>
  );
}

// ---------- contrast nudge ----------

function ContrastNudge({
  checks,
}: {
  readonly checks: readonly ContrastCheck[];
}) {
  const failing = checks.filter((c) => !c.passesAA);
  if (failing.length === 0) {
    return (
      <Text as="p" variant="bodySm" color="success">
        ✓ Text contrast meets WCAG AA on every surface.
      </Text>
    );
  }
  return (
    <Callout variant="warning" align="start">
      <Stack gap="xs">
        <Text variant="bodySm">
          <Text variant="bodySm" weight="semibold">
            Low contrast
          </Text>{" "}
          — harder to read for some visitors:
        </Text>
        {failing.map((c) => (
          <Text key={c.label} variant="bodySm">
            {c.label}: {c.ratio.toFixed(1)}:1 (aim for 4.5:1)
          </Text>
        ))}
      </Stack>
    </Callout>
  );
}

// ---------- live preview ----------

function Preview({
  cssVars,
  tokens,
}: {
  readonly cssVars: Record<string, string>;
  readonly tokens: Tokens;
}) {
  const steps = ["100", "300", "500", "700", "900"] as const;
  return (
    <>
      {/* eslint-disable-next-line duro/no-raw-html-element -- spreads the site's runtime `--wb-*` custom properties, which RSD's StyleX-only `style` prop cannot carry (same as PageNode's canvas root). */}
      <div style={cssVars as React.CSSProperties}>
        <html.div style={[s.previewFrame, s.previewSurface]}>
          <html.div style={s.previewBody}>
            <html.div style={s.previewHeading}>Your heading</html.div>
            <html.p style={s.previewCopy}>
              A line of body copy shows how text reads on your background.
            </html.p>
            <html.button type="button" style={s.previewButton}>
              Primary button
            </html.button>
          </html.div>
          <html.div style={s.ramp}>
            {steps.map((step) => (
              <html.div
                key={step}
                aria-label={`primary ${step}`}
                style={[s.rampStep, s.rampColor(tokens.color.primary[step])]}
              />
            ))}
          </html.div>
        </html.div>
      </div>
    </>
  );
}

// ---------- styles ----------

function normalizeHex(v: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(v) ? v : "#000000";
}

function capitalize(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

// No design-system values here: the editor chrome is Duro components, the
// swatch is geometry (em, so it scales with the chip's text), and the preview
// reads the SITE's `--wb-*` variables because it shows the site, not the
// editor.
const s = css.create({
  swatch: {
    width: "0.9em",
    height: "0.9em",
    borderRadius: "50%",
    flexShrink: 0,
  },
  swatchColors: (brand: string, accent: string) => ({
    backgroundColor: brand,
    boxShadow: `inset 0 0 0 0.15em ${accent}`,
  }),
  // The frame is the SITE's: its border and radius, not the editor's.
  previewFrame: {
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: "var(--wb-color-border)",
    borderRadius: "var(--wb-radius-md)",
    overflow: "hidden",
  },
  previewSurface: {
    backgroundColor: "var(--wb-color-background)",
    color: "var(--wb-color-foreground)",
    fontFamily: "var(--wb-font-body)",
  },
  previewBody: {
    padding: "var(--wb-space-lg)",
  },
  previewHeading: {
    fontFamily: "var(--wb-font-heading)",
    fontSize: "var(--wb-font-size-2xl)",
    fontWeight: "var(--wb-font-weight-bold)",
    lineHeight: "var(--wb-line-height-tight)",
  },
  previewCopy: {
    marginTop: "var(--wb-space-sm)",
    marginBottom: "var(--wb-space-md)",
    marginLeft: 0,
    marginRight: 0,
  },
  previewButton: {
    backgroundColor: "var(--wb-color-primary)",
    color: "var(--wb-color-primary-foreground)",
    borderWidth: 0,
    borderRadius: "var(--wb-radius-md)",
    paddingTop: "var(--wb-space-sm)",
    paddingBottom: "var(--wb-space-sm)",
    paddingLeft: "var(--wb-space-lg)",
    paddingRight: "var(--wb-space-lg)",
    fontFamily: "inherit",
    fontSize: "inherit",
    cursor: "default",
  },
  ramp: {
    display: "flex",
  },
  // Five equal steps. Not flexGrow: react-strict-dom forces flex-grow 0 on
  // web (duro/no-flex-grow-web).
  rampStep: {
    width: "20%",
    height: "1.1em",
  },
  rampColor: (bg: string) => ({
    backgroundColor: bg,
  }),
});
